<script setup lang="ts">
import { shallowRef } from 'vue'
import {
  ExclamationCircleFilled,
  ReadOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons-vue'
import { downloadOperationManual } from '@/api/systemConfig'
import { useMessage } from '@/composables/useMessage'

defineOptions({ name: 'HeaderManualDownload' })

const { msg } = useMessage()
const noticeOpen = shallowRef(false)
const acknowledged = shallowRef(false)
const downloading = shallowRef(false)

function openNotice() {
  if (downloading.value) return
  acknowledged.value = false
  noticeOpen.value = true
}

function closeNotice() {
  if (downloading.value) return
  noticeOpen.value = false
}

async function confirmDownload() {
  if (!acknowledged.value || downloading.value) return
  downloading.value = true
  try {
    await downloadOperationManual()
    noticeOpen.value = false
    msg.success('已开始下载操作手册')
  } catch (e) {
    const m = e instanceof Error ? e.message.trim() : ''
    if (m) msg.error(m)
  } finally {
    downloading.value = false
  }
}
</script>

<template>
  <button
    type="button"
    class="header-manual-trigger"
    aria-label="下载系统操作手册"
    aria-haspopup="dialog"
    :aria-expanded="noticeOpen"
    title="下载系统操作手册"
    @click="openNotice"
  >
    <ReadOutlined />
  </button>

  <a-modal
    v-model:open="noticeOpen"
    title="下载注意事项"
    width="440px"
    :mask-closable="false"
    :keyboard="!downloading"
    :closable="!downloading"
    centered
    wrap-class-name="header-manual-notice-wrap"
    @cancel="closeNotice"
  >
    <div class="manual-notice">
      <div class="manual-notice__banner" role="note">
        <ExclamationCircleFilled class="manual-notice__banner-icon" aria-hidden="true" />
        <div>
          <p class="manual-notice__kicker">
            <SafetyCertificateOutlined aria-hidden="true" />
            内部资料
          </p>
          <p class="manual-notice__lead">本手册仅限内部工作使用，切勿外传。</p>
        </div>
      </div>
      <ul class="manual-notice__list">
        <li>文件仅供本系统工作人员查阅与培训，不得转发至互联网或非授权渠道。</li>
        <li>请遵守「涉密不上网，上网不涉密」，不得在手册中填入、保存涉密内容。</li>
        <li>下载后请妥善保管，使用完毕按单位规定处理。</li>
      </ul>
      <a-checkbox v-model:checked="acknowledged" :disabled="downloading">
        我已阅读并知悉：内部资料，切勿外传
      </a-checkbox>
    </div>
    <template #footer>
      <a-button :disabled="downloading" @click="closeNotice">取消</a-button>
      <a-button
        type="primary"
        :disabled="!acknowledged"
        :loading="downloading"
        @click="confirmDownload"
      >
        确认下载
      </a-button>
    </template>
  </a-modal>
</template>

<style scoped>
.header-manual-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  padding: 0;
  color: var(--nav-header-text-muted);
  font-size: 17px;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
  transition:
    color 0.2s ease,
    background 0.2s ease;
}

.header-manual-trigger:hover {
  color: var(--nav-header-text);
  background: var(--nav-header-hover-bg);
}

.header-manual-trigger:focus-visible {
  outline: 2px solid #38bdf8;
  outline-offset: 2px;
}

.manual-notice {
  display: flex;
  flex-direction: column;
  gap: 14px;
  color: #020617;
}

.manual-notice__banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid #f59e0b;
  background: #fffbeb;
}

.manual-notice__banner-icon {
  margin-top: 2px;
  font-size: 18px;
  color: #d97706;
}

.manual-notice__kicker {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #92400e;
}

.manual-notice__lead {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.5;
  color: #0f172a;
}

.manual-notice__list {
  margin: 0;
  padding-left: 1.15em;
  font-size: 13px;
  line-height: 1.65;
  color: #334155;
}

.manual-notice :deep(.ant-checkbox-wrapper) {
  font-size: 13px;
  color: #0f172a;
  cursor: pointer;
}

@media (max-width: 767px) {
  .header-manual-trigger {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .header-manual-trigger {
    transition: none;
  }
}
</style>
